import React, { useEffect, useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { Users, TrendingUp, Calendar, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n || 0);

export default function ShopCustomersPage() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    setLoading(true);
    shopApi.get('/shop/admin/customers/analytics')
      .then(r => setList(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const openCustomer = async (c) => {
    setSelected(c);
    try {
      const r = await shopApi.get(`/shop/admin/customers/${c.customer_id}/orders`);
      setOrders(r.data);
    } catch { setOrders([]); }
  };

  const filtered = list.filter(c => !search ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><Users className="w-7 h-7 text-primary" />Customers</h1>
        <p className="text-sm text-muted-foreground">Spending, frequency, top items and next-purchase predictions.</p>
      </div>

      <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="customers-search" />

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr className="text-left">
              <th className="p-3">Customer</th>
              <th className="p-3 text-right">Orders</th>
              <th className="p-3 text-right">Total spent</th>
              <th className="p-3 text-right">Avg order</th>
              <th className="p-3 text-right">Per month</th>
              <th className="p-3">Last order</th>
              <th className="p-3">Predicted next</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="customers-table-body">
            {loading ? (
              <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No registered customer orders yet.</td></tr>
            ) : filtered.map(c => (
              <tr key={c.customer_id} className="border-t border-border hover:bg-muted/20" data-testid={`customer-row-${c.customer_id}`}>
                <td className="p-3"><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.email}</div></td>
                <td className="p-3 text-right">{c.order_count}</td>
                <td className="p-3 text-right font-semibold">RWF {formatRWF(c.total_spent)}</td>
                <td className="p-3 text-right">RWF {formatRWF(c.avg_order_value)}</td>
                <td className="p-3 text-right">{c.orders_per_month}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.days_since_last}d ago</td>
                <td className="p-3 text-xs">
                  {c.predicted_next_item ? (
                    <div>
                      <div className="font-medium flex items-center gap-1"><Sparkles className="w-3 h-3 text-primary" />{c.predicted_next_item}</div>
                      {c.predicted_in_days !== null && c.predicted_in_days !== undefined && (
                        <div className="text-muted-foreground">in ~{c.predicted_in_days}d</div>
                      )}
                    </div>
                  ) : <span className="text-muted-foreground">–</span>}
                </td>
                <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => openCustomer(c)} data-testid={`customer-view-${c.customer_id}`}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
                <DialogDescription>Spending overview, top items and order history for this customer.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-3 gap-3">
                  <Stat icon={TrendingUp} label="Total spent" value={`RWF ${formatRWF(selected.total_spent)}`} />
                  <Stat icon={Calendar} label="Orders" value={selected.order_count} />
                  <Stat icon={Award} label="Avg / order" value={`RWF ${formatRWF(selected.avg_order_value)}`} />
                </div>
                <div>
                  <div className="font-semibold mb-2 flex items-center gap-1"><Award className="w-4 h-4 text-primary" />Top items</div>
                  {selected.top_items.length === 0 ? <div className="text-muted-foreground text-xs">No items yet.</div> : (
                    <ul className="text-sm space-y-1">
                      {selected.top_items.map(it => (
                        <li key={it.product_id} className="flex justify-between"><span>{it.product_name}</span><span className="text-muted-foreground">{it.total_quantity} units</span></li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="font-semibold mb-2">Order history</div>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {orders.map(o => (
                      <div key={o.id} className="flex justify-between text-xs p-2 rounded bg-muted/30">
                        <span>#{o.order_number} · {new Date(o.created_at).toLocaleDateString()}</span>
                        <span className="font-mono">RWF {formatRWF(o.total)} · <span className="capitalize text-muted-foreground">{o.status.replace(/_/g,' ')}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-3 rounded-lg bg-muted/30 border border-border">
    <div className="text-xs text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
    <div className="text-lg font-bold mt-0.5">{value}</div>
  </div>
);
